import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Phone, 
  Star, 
  Shield, 
  Clock, 
  Award,
  Sparkles,
  Wrench,
  PaintBucket,
  Building,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useContent } from '../contexts/ContentContext';

const HERO_FALLBACK = "/images/006-5434-Abington-Creek-Ln-Sugar-Land.jpg";

const services = [
  {
    icon: Sparkles,
    title: 'Residential Pool Cleaning',
    description: 'Weekly professional cleaning to keep your pool crystal clear and swim-ready.',
    image: '/images/016-5503-Harvest-Oak-Landing-Dr-Spring.jpg',
    span: 'sm:col-span-2 sm:row-span-2',
    link: '/services/residential'
  },
  {
    icon: Wrench,
    title: 'Repair & Maintenance',
    description: 'Expert repairs for pumps, filters, and pool equipment.',
    image: '/images/45_Landry.jpg',
    span: 'sm:col-span-1 sm:row-span-1',
    link: '/services/repair-maintenance'
  },
  {
    icon: PaintBucket,
    title: 'Pool Renovations',
    description: 'Resurfacing, tile work, and modern upgrades.',
    image: '/images/Wolf_b-1.jpg',
    span: 'sm:col-span-1 sm:row-span-1',
    link: '/services/renovations'
  },
  {
    icon: Building,
    title: 'Commercial Services',
    description: 'Professional pool maintenance for hotels, apartments, and businesses.',
    image: '/images/467191288_560975559913547_5030626414160754671_n.jpg',
    span: 'sm:col-span-2 sm:row-span-1',
    link: '/services/commercial'
  },
];

const features = [
  { icon: Shield, title: 'Licensed & Insured', description: 'Fully certified technicians' },
  { icon: Clock, title: 'Reliable Service', description: 'On-time, every time' },
  { icon: Award, title: 'Quality Guaranteed', description: 'Satisfaction guaranteed' },
  { icon: Star, title: '5-Star Rated', description: 'Trusted by thousands' },
];

const defaultTestimonials = [
  {
    name: 'Sarah M.',
    location: 'Tampa, FL',
    text: "A-Quality has been maintaining our pool for 3 years. They're always on time, professional, and our pool has never looked better!",
    rating: 5
  },
  {
    name: 'Mike R.',
    location: 'Brandon, FL',
    text: "Best pool service in the Tampa Bay area. They fixed an issue with our pump that two other companies couldn't figure out.",
    rating: 5
  },
  {
    name: 'Jennifer L.',
    location: 'Clearwater, FL',
    text: "Switched to A-Quality last year and wish we had done it sooner. Crystal clear water every week and great communication.",
    rating: 5
  },
];

export const Home = () => {
  const { homepage, global } = useContent();

  return (
    <div data-testid="home-page">
      <Helmet>
        <title>A-Quality Pool Service | Professional Pool Cleaning & Maintenance in Tampa Bay, FL</title>
        <meta name="description" content="A-Quality Pool Service offers professional pool cleaning, repair, maintenance & renovation in Tampa Bay and Central Florida. A Cody Pools Company. Call 813-453-5988 for a free quote." />
        <link rel="canonical" href="https://a-qualitypools.net/" />
      </Helmet>
      <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_FALLBACK}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            fetchPriority="high"
            width={1920}
            height={1080}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/40 to-slate-900/70" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <motion.span
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-block text-sky-400 font-medium mb-4"
            >
              {homepage.heroTagline}
            </motion.span>
            
            <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              {homepage.heroHeadline}{' '}
              <span className="text-sky-400">{homepage.heroHeadlineHighlight}</span>
            </h1>
            
            <p className="text-base sm:text-xl text-slate-200 mb-8 max-w-2xl">
              {homepage.heroSubtext}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                data-testid="hero-cta-quote"
                className="bg-sky-500 hover:bg-sky-400 text-white rounded-full px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg cta-pulse"
              >
                <Link to="/contact">
                  Get Your Free Quote
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              
              <Button
                asChild
                variant="outline"
                data-testid="hero-cta-call"
                className="border-2 border-white text-white hover:bg-white hover:text-slate-900 rounded-full px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg bg-transparent"
              >
                <a href={`tel:${global.phone}`}>
                  <Phone className="mr-2 h-5 w-5" />
                  {global.phone}
                </a>
              </Button>
            </div>
          </motion.div>

          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-16 flex flex-wrap gap-8"
          >
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="flex items-center text-white/80"
              >
                <feature.icon className="h-6 w-6 mr-2 text-sky-400" />
                <span className="font-medium">{feature.title}</span>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2"
          >
            <motion.div className="w-1.5 h-3 bg-white/50 rounded-full" />
          </motion.div>
        </motion.div>
      </section>

      {/* Services Section - Bento Grid */}
      <section data-testid="services-section" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12 sm:mb-16"
          >
            <span className="text-sky-500 font-medium mb-2 block">What We Do</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              {homepage.servicesTitle}
            </h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              {homepage.servicesSubtitle}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 auto-rows-[180px] sm:auto-rows-[200px]">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`${service.span} relative group`}
              >
                <Link
                  to={service.link}
                  data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="block h-full"
                >
                  <Card className="h-full overflow-hidden border-0 shadow-lg service-card">
                    <div className="absolute inset-0">
                      <img
                        src={service.image}
                        alt={service.title}
                        loading="lazy"
                        decoding="async"
                        width={1200}
                        height={800}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent" />
                    </div>
                    <CardContent className="relative h-full flex flex-col justify-end p-4 sm:p-6">
                      <div className="p-2 sm:p-3 bg-sky-500 rounded-xl w-fit mb-2 sm:mb-3">
                        <service.icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                      </div>
                      <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-1 sm:mb-2">
                        {service.title}
                      </h3>
                      <p className="text-slate-200 text-xs sm:text-sm md:text-base hidden sm:block">
                        {service.description}
                      </p>
                      <div className="mt-2 sm:mt-4 flex items-center text-sky-400 font-medium group-hover:translate-x-2 transition-transform text-sm sm:text-base">
                        Learn More <ChevronRight className="ml-1 h-4 w-4" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section data-testid="why-us-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <span className="text-sky-500 font-medium mb-2 block">Why Choose Us</span>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                {homepage.whyUsTitle}
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                {homepage.whyUsText}
              </p>

              <div className="grid sm:grid-cols-2 gap-6">
                {features.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start p-4 bg-slate-50 rounded-2xl"
                  >
                    <div className="p-3 bg-sky-500/10 rounded-xl mr-4">
                      <feature.icon className="h-6 w-6 text-sky-500" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">{feature.title}</h4>
                      <p className="text-sm text-slate-600">{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <Button
                asChild
                data-testid="why-us-cta"
                className="mt-8 bg-sky-500 hover:bg-sky-400 text-white rounded-full px-8"
              >
                <Link to="/about">Learn More About Us</Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/images/006-5434-Abington-Creek-Ln-Sugar-Land.jpg"
                  alt="Beautiful Florida pool"
                  loading="lazy"
                  decoding="async"
                  width={1200}
                  height={802}
                  className="w-full h-[500px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl">
                <div className="flex items-center space-x-4">
                  <div className="p-4 bg-sky-500 rounded-xl">
                    <Award className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">15+ Years</p>
                    <p className="text-slate-600">of Excellence</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section data-testid="testimonials-section" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12 sm:mb-16"
          >
            <span className="text-sky-500 font-medium mb-2 block">Testimonials</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              {homepage.testimonialsTitle}
            </h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              {homepage.testimonialsSubtext}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {defaultTestimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                data-testid={`testimonial-${index}`}
              >
                <Card className="h-full bg-white border-slate-200 shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-8">
                    {/* Google-style header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-sky-500 flex items-center justify-center text-white font-semibold text-lg">
                          {testimonial.name.charAt(0)}
                        </div>
                        <div className="ml-3">
                          <p className="font-semibold text-slate-900">{testimonial.name}</p>
                          <p className="text-sm text-slate-500">{testimonial.location}</p>
                        </div>
                      </div>
                      <img 
                        src="https://www.google.com/images/branding/googleg/1x/googleg_standard_color_128dp.png" 
                        alt="Google" 
                        width={24}
                        height={24}
                        loading="lazy"
                        className="h-6 w-6"
                      />
                    </div>
                    
                    {/* 5 Stars */}
                    <div className="flex items-center mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                      ))}
                      <span className="ml-2 text-sm text-slate-500">5.0</span>
                    </div>
                    
                    {/* Review text */}
                    <p className="text-slate-700 leading-relaxed">{testimonial.text}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="pt-20 pb-36 bg-sky-500 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23000" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          }} />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6">
              {homepage.ctaHeadline}
            </h2>
            <p className="text-base sm:text-xl text-white/90 mb-6 sm:mb-8">
              {homepage.ctaSubtext}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                data-testid="cta-get-quote"
                className="bg-white text-sky-600 hover:bg-slate-100 rounded-full px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg font-semibold"
              >
                <Link to="/contact">Get Your Free Quote</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="cta-call"
                className="border-2 border-white text-white hover:bg-white hover:text-sky-600 rounded-full px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg bg-transparent"
              >
                <a href={`tel:${global.phone}`}>
                  <Phone className="mr-2 h-5 w-5" />
                  Call {global.phone}
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
